import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/lenis";

const SECTION_LINKS = [
  { id: "systems", label: "Systems", testid: "nav-link-systems" },
  { id: "kits", label: "Compare Kits", testid: "nav-link-compare" },
  { id: "calculator", label: "Calculator", testid: "nav-link-calculator" },
  { id: "faq", label: "FAQ", testid: "nav-link-faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-slate-400/10 bg-panel/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="The Mechanic's Tool Kit home">
          <Logo compact />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {SECTION_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => goSection(l.id)}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 hover:text-blaze transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/free"
            data-testid="nav-link-free"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-blaze hover:text-orange-300 transition-colors duration-200"
          >
            Free Sample
          </Link>
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 hover:text-blaze transition-colors duration-200"
          >
            Contact
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => goSection("kits")}
            className="hidden sm:inline-flex items-center gap-2 bg-blaze text-white font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)]"
          >
            <Zap className="w-4 h-4" /> Instant Access
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-slate-300 p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-slate-400/10 bg-panel/95 backdrop-blur-xl"
          >
            <div className="px-5 py-4 flex flex-col gap-4">
              {SECTION_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => goSection(l.id)}
                  className="text-left font-mono text-xs uppercase tracking-[0.22em] text-slate-300"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/free"
                data-testid="nav-mobile-free"
                onClick={() => setOpen(false)}
                className="font-mono text-xs uppercase tracking-[0.22em] text-blaze"
              >
                Free Sample
              </Link>
              <Link
                to="/contact"
                data-testid="nav-mobile-contact"
                onClick={() => setOpen(false)}
                className="font-mono text-xs uppercase tracking-[0.22em] text-slate-300"
              >
                Contact
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
